import classNames from 'classnames';
import { Fragment } from 'react';

import type { TimelineLevel } from '../../../../common/api/loyalty/externalLoyaltyModels';

import VoucherSplash from '../../VoucherSplash/VoucherSplash';
import Styles from './ProgressBar.module.scss';
import { getVoucherSplashSize, getVoucherSplashTop } from './ProgressBar.utils';
import { useGetBonusLevels } from './useProgressBar';
import { useProgressBarWidth } from './useProgressBarWidth';

type ProgressBarProps = {
    levels: TimelineLevel[];
    bonusProgressInfoDays: string;
    bonusProgressMonetary: string;
};

const ProgressBar = ({
    levels,
    bonusProgressInfoDays,
    bonusProgressMonetary,
}: ProgressBarProps) => {
    const { barRef, isWide } = useProgressBarWidth();
    const { bonusLevels, progressWidth, dots, haveCompletedLevels } = useGetBonusLevels(levels);

    const voucherSplashSize = getVoucherSplashSize(isWide);
    const voucherSplashTop = getVoucherSplashTop(isWide);

    const lastIndex =
        bonusLevels.length - 1 - [...bonusLevels].reverse().findIndex((item) => item.completed);

    return (
        <>
            <div className={Styles.ProgressBarContainer}>
                <div
                    className={classNames(Styles.ProgressBarWrapper, {
                        [Styles.LargeMarginTop]: haveCompletedLevels,
                    })}
                >
                    <div ref={barRef} className={Styles.Bar}>
                        {bonusLevels.map((item, i) => (
                            <Fragment key={item.order}>
                                {(item.order === 0 || i !== 0) && (
                                    <div
                                        className={Styles.Text}
                                        style={{
                                            left: `${dots[i]}%`,
                                            top: i === lastIndex ? voucherSplashTop : '-50px',
                                        }}
                                    >
                                        {i === lastIndex ? (
                                            <>
                                                <VoucherSplash
                                                    value={item.rewardLabel}
                                                    unit="kr"
                                                    size={voucherSplashSize}
                                                    ariaHidden={true}
                                                />
                                                <div className="u-hiddenVisually">
                                                    Din bonus just nu. {item.rewardLabel} kronor.
                                                </div>
                                            </>
                                        ) : (
                                            (!haveCompletedLevels || i > lastIndex) && (
                                                <div className={Styles.NextLevelText} aria-hidden>
                                                    {item.rewardLabel}
                                                    <br />
                                                    kr
                                                </div>
                                            )
                                        )}
                                    </div>
                                )}
                                <div
                                    className={classNames(Styles.Level, {
                                        [Styles.Completed]: item.completed,
                                    })}
                                    style={{
                                        left: `${dots[i]}%`,
                                    }}
                                    aria-hidden
                                >
                                    <div className={Styles.Dot} />
                                </div>
                            </Fragment>
                        ))}
                        <div
                            className={classNames(Styles.CurrentLevel, {
                                [Styles.SecondLevel]: bonusLevels[0].order >= 1,
                            })}
                            style={{
                                ['--target-progress-width' as string]: `${progressWidth}%`,
                            }}
                            aria-hidden
                        />
                    </div>
                </div>
            </div>
            <div className={Styles.RemainingLines}>
                <p>
                    {bonusProgressInfoDays}
                    <br />
                    {bonusProgressMonetary}
                </p>
            </div>
        </>
    );
};

export default ProgressBar;
